import React, { useState, useRef, useEffect } from "react";
import ImportFileModalComponent from "components/schedule/react-scheduler/forms/import/ImportFileModalComponent";
import ImportLoadingModalComponent from "components/schedule/react-scheduler/forms/import/ImportLoadingModalComponent";
import ImportConfirmationModalComponent from "components/channel-profiles/business-rules/modals/ImportConfirmationModalComponent";
import ImportResultModalComponent from "./ImportResultModalComponent";
import styles from "../../schedule/react-scheduler/forms/import/ImportSlots.module.css";
import { ProgramTypes } from "../ChannelArchitecture.d";

export function ImportComponent({
  closeModal,
  scheduleInfo,
  schedulerData,
  setIsDirty,
  sampleTemplate,
  handleOnImportApi,
  importResultColumns,
  importResultFileTitle,
  dataExists,
  dataModel,
  setSchedules,
}) {
  const { channelId } = { ...scheduleInfo };
    const { seriesList, titleGroupList } = {
    ...schedulerData,
  };

  const [selectedFile, setSelectedFile] = useState();
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [importResultData, setImportResultData] = useState([]);
  const [overwriteModal, setOverwriteModal] = useState({
    show: false,
    overwriteData: [],
  });
  const [isImporting, setIsImporting] = useState(false);

  const loadingHeaderComponent = (
    <div className={styles.divHeader}>
      Importing and validating slots from{" "}
      <b>{selectedFile && selectedFile.name}</b>
    </div>
  );

  const removeImportAndResultFields = (data) => {
    let temp = data;
    let removedFailedImports = temp.filter((schedule) => schedule.imported);

    const cleanedData = removedFailedImports.map((schedule) => {
      let newObj = Object.keys(schedule).reduce((object, key) => {
        if (key !== "imported" && key !== "result" && key !== "seasonNo") {
          object[key] = schedule[key];
        }
        return object;
      }, {});
      let titleGroup = { id: null, name: null };
      let series = { id: null, name: null };
      let seasons = { id: null, name: null };
      if (newObj.titleGroupName) {
        titleGroup = titleGroupList.find(
          (titleGroup) => titleGroup.name === newObj.titleGroupName
        );
      }

      if (newObj.seriesName) {
        // Find the series object based on the seriesName in the newObj
        series = seriesList.find((item) => item.name === newObj.seriesName);

        // Find the season object based on the seasonID in the newObj, if it exists
        if (newObj.seasonID) {
          seasons = series.seasons.find(
            (item) => item.id === parseInt(newObj.seasonID, 10)
          );
        }
      }
      return {
        ...newObj,
        titleGroupID: titleGroup?.id,
        titleGroupName: titleGroup?.name,
        seriesID: series?.id,
        seriesName: series?.name,
        seasonID: seasons?.id,
        seasonName: seasons?.name,
      };
    });
    return cleanedData;
  };

  const uploadFile = async (file) => {
    setIsImporting(true);

    try {
      const response = await handleOnImportApi(channelId, file);

      if (response.status !== 200) {
        danger("Error occurred while importing file.");
        setIsImporting(false);
        return;
      }

      const data = response.data[dataModel].map((o) => ({
        ...o,
        week: 1,
        dayOfWeek: parseInt(o.dayOfWeek),
        type: o.type === "TitleGroup" ? ProgramTypes.TITLEGROUP : o.type,
        id: window.crypto.randomUUID(),
      }));

      setIsImporting(false);

      if (response.data[dataExists]) {
        setOverwriteModal({
          show: true,
          overwriteData: data,
        });
        return;
      }

      setImportResultData(data);
      setOverwriteModal({
        show: false,
        overwriteData: [],
      });
      setSchedules(removeImportAndResultFields(data));
      setUploadSuccess(true);
    } catch (error) {
      console.error(error);

      if (error.message.includes("400")) {
        setErrorMessage(
          "Invalid file format. Please use files in the suggested format above (see sample template)"
        );
      } else if (error.message.includes("500")) {
        setErrorMessage("Error uploading file. Please try again later");
      }

      setOverwriteModal({
        show: false,
        overwriteData: [],
      });
      setIsDirty(false);
      setUploadSuccess(false);
      setIsImporting(false);
    }
  };

  const handleOnImportFile = () => {
    const acceptableFiles = ["xlsx", "xls", "csv"];
    const fileExtension = selectedFile.name.split(".").pop();
    if (acceptableFiles.includes(fileExtension)) {
      uploadFile(selectedFile);
    } else {
      setErrorMessage(
        "Uploaded file type is not supported. Please use files in the following format (xlsx, csv)"
      );
    }
  };

  const handleOverwrite = (overwriteData) => {
    setOverwriteModal({
      show: false,
      overwriteData: [],
    });
    setImportResultData(overwriteData);
    setSchedules(removeImportAndResultFields(overwriteData));
    setUploadSuccess(true);
  };

  const handleCloseOverwriteModal = () => {
    setIsDirty(false);
    closeModal();
  };

  return isImporting ? (
    <ImportLoadingModalComponent headerComponent={loadingHeaderComponent} />
  ) : uploadSuccess ? (
    <ImportResultModalComponent
      importResultData={importResultData}
      closeModal={closeModal}
      scheduleInfo={scheduleInfo}
      importResultColumns={importResultColumns}
      importResultFileTitle={importResultFileTitle}
    />
  ) : overwriteModal.show ? (
    <ImportConfirmationModalComponent
      show={overwriteModal.show}
      onHide={handleCloseOverwriteModal}
      onProceed={() => handleOverwrite(overwriteModal.overwriteData)}
      message="A list already exists for this channel. Importing overwrites all existing data for this channel."
    />
  ) : (
    <ImportFileModalComponent
      closeModal={closeModal}
      selectedFile={selectedFile}
      setSelectedFile={(e) => {
        setSelectedFile(e);
        setErrorMessage("");
      }}
      handleOnImportFile={handleOnImportFile}
      errorMessage={errorMessage}
      setErrorMessage={(e) => setErrorMessage(e)}
      sampleTemplate={sampleTemplate}
    />
  );
}
